import { formatRatePercent } from './TariffCodePicker';

/** Structurally matches the SDK CustomsEstimate. Amounts are USD. */
export interface CustomsEstimateView {
  isExempt: boolean;
  exemptionThresholdUsd: number;
  rates: { importDutyRate: number; scfRate: number; environmentalLevy: number; exciseDuty: number; gctRate: number };
  breakdown: { importDuty: number; scf: number; environmentalLevy: number; exciseDuty: number; gct: number };
  totalCustomsUsd: number;
  disclaimer?: string;
}

export interface CustomsBreakdownLabels {
  title: string;
  importDuty: string;
  scf: string;
  environmentalLevy: string;
  exciseDuty: string;
  gct: string;
  total: string;
  exempt: (thresholdUsd: number) => string;
  disclaimer: string;
}

const DEFAULT_LABELS: CustomsBreakdownLabels = {
  title: 'Estimated customs',
  importDuty: 'Import Duty',
  scf: 'Standard Compliance Fee',
  environmentalLevy: 'Environmental Levy',
  exciseDuty: 'Excise Duty',
  gct: 'GCT',
  total: 'Estimated customs total',
  exempt: (t) => `No customs: items valued at US$${t} or less are exempt.`,
  disclaimer: 'Estimate only. Final duties and taxes are assessed by Jamaica Customs on arrival and may differ.',
};

export interface CustomsBreakdownProps {
  estimate: CustomsEstimateView;
  /** Formats a USD amount for display (the app applies its exchange rate / currency). */
  formatMoney: (usd: number) => string;
  labels?: Partial<CustomsBreakdownLabels>;
  className?: string;
}

export function CustomsBreakdown({ estimate, formatMoney, labels, className = '' }: CustomsBreakdownProps) {
  const l = { ...DEFAULT_LABELS, ...labels };
  const { rates, breakdown } = estimate;
  const rows = [
    { label: `${l.importDuty} (${formatRatePercent(rates.importDutyRate)})`, value: breakdown.importDuty, show: true },
    { label: `${l.scf} (${formatRatePercent(rates.scfRate)})`, value: breakdown.scf, show: rates.scfRate > 0 },
    { label: `${l.environmentalLevy} (${formatRatePercent(rates.environmentalLevy)})`, value: breakdown.environmentalLevy, show: rates.environmentalLevy > 0 },
    { label: `${l.exciseDuty} (${formatRatePercent(rates.exciseDuty)})`, value: breakdown.exciseDuty, show: breakdown.exciseDuty > 0 },
    { label: `${l.gct} (${formatRatePercent(rates.gctRate)})`, value: breakdown.gct, show: true },
  ].filter((r) => r.show);

  return (
    <div className={`rounded-lg border border-gray-200 dark:border-gray-700 p-3 text-sm ${className}`} data-testid="customs-breakdown">
      <div className="font-medium text-gray-900 dark:text-white mb-2">{l.title}</div>
      {estimate.isExempt ? (
        <p className="text-green-700 dark:text-green-400">{l.exempt(estimate.exemptionThresholdUsd)}</p>
      ) : (
        <dl className="space-y-1">
          {rows.map((r) => (
            <div key={r.label} className="flex justify-between gap-3 text-gray-700 dark:text-gray-300">
              <dt>{r.label}</dt>
              <dd className="tabular-nums">{formatMoney(r.value)}</dd>
            </div>
          ))}
          <div className="flex justify-between gap-3 border-t border-gray-200 dark:border-gray-700 pt-1 font-semibold text-gray-900 dark:text-white">
            <dt>{l.total}</dt>
            <dd className="tabular-nums">{formatMoney(estimate.totalCustomsUsd)}</dd>
          </div>
        </dl>
      )}
      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{estimate.disclaimer || l.disclaimer}</p>
    </div>
  );
}

export default CustomsBreakdown;
